import { useState, useRef } from "react";
import { useFirestoreGeneralOrderBy, useFirestore, useFirestoreGeneral } from "../../firebase/useFirestore";
import useSettings from "../../hooks/Settings";
import { db } from "../../firebase/config";
import { client } from "../../hooks/Client";
import { buildAdressLabelMap, buildAdressContactMap, buildMaterialsExportRows } from '../../helpers/materialsHelpers/materialsExport';
import { HOUSEHOLD_FIELDS, ADDRESS_LABEL_TITLES, LABEL_LEGACY_TITLES } from '../../helpers/adressHelpers/householdFields';
import loadingSpinner from '../../images/spinner-ripple.svg'
import notDeleted from '../../helpers/notDeleted'
import buildResearchResultRows from '../../helpers/dataHelpers/buildResearchResultRows';
import downloadCSV from '../../helpers/export/downloadCSV';
import downloaderFor from '../../helpers/export/downloaderFor';
import callLogRecordOpen from '../../helpers/clientRecordAudit/callLogRecordOpen';

const MATERIALS_HEADER = ['Adres', 'Naam', 'Telefoonnummer', 'Materiaal', 'Productnummer', 'Soort', 'Categorie', 'Aantal', 'Eenheid', 'Status', 'Omschrijving', 'Prijs per stuk', 'Subtotaal'];

// De PersonaData-titels die de materialenexport nodig heeft: het adreslabel
// plus de naam en het telefoonnummer van de bewoner. Afgeleid uit dezelfde
// constanten die de schermen gebruiken, zodat de query niet stilletjes een
// titel mist die een lezer wél accepteert.
//
// Let op de bovengrens: een Firestore `in`-filter neemt maximaal 10 waarden.
// Vaste gegevens (T9): adres, naam en telefoon staan op het huishouden; deze
// oude titels zijn de terugval tot de migratie. Afgeleid uit het schema.
const ADRESS_FIELD_TITLES = [...new Set([
  ...ADDRESS_LABEL_TITLES,
  ...LABEL_LEGACY_TITLES,
  ...HOUSEHOLD_FIELDS.Telefoon.legacyTitles,
])];

const Data = ({selectedOption}) => {
  // State
  const [outputResultsArray, setOutputResultsArray] = useState([]);
  const [researchResultsArray, setResearchResultsArray] = useState([]);
  const [researchHeaderOrder, setResearchHeaderOrder] = useState([]); // ⟵ ordered CSV header for research
  const [datasetCreated, setDatasetCreated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingResearch, setLoadingResearch] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [selectedActivityTitle, setSelectedActivityTitle] = useState('');
  const [selectedResearch, setSelectedResearch] = useState('');
  const [selectedResearchTitle, setSelectedResearchTitle] = useState('');
  const [researchDataCreated, setResearchDataCreated] = useState(false);
  const [materialsArray, setMaterialsArray] = useState([]);
  const [materialsCreated, setMaterialsCreated] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  // Hooks
  const companyName = useSettings().compagnyName;
  const options = { year: 'numeric', month: 'numeric', day: 'numeric' };
  const optionsLong = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' };

  // Firestore
  const questionnairesResponses = useFirestoreGeneralOrderBy('QuestionnairesResponses', 'ResearchID', selectedResearch || "", 'Timestamp', 'desc');
  const outputs = useFirestoreGeneral('Outputs', 'ActivityID', selectedActivity || '');
  const activities = useFirestore('Activities');
  const researchRaw = useFirestore('Research');
  const research = (researchRaw || []).filter((r) => !r.Deleted);
  const momentsRaw = useFirestoreGeneral('MeasureMoments', 'ResearchID', selectedResearch || "");
  const moments = (momentsRaw || []).filter((m) => !m.Deleted);

  // -------- OUTPUTS --------
  const resetOutputsState = () => {
    setOutputResultsArray([]);
    setDatasetCreated(false);
    setLoading(false);
    setSelectedActivity('');
    setSelectedActivityTitle('');
  };

  /**
   * Zelfde keuze als bij de onderzoeksresultaten: een CSV landt in een
   * Nederlandse Excel in één kolom.
   * @param {'xlsx' | 'csv'} format - Het bestandsformaat dat de gebruiker koos
   */
  const downloadOutputResults = (format) => {
    const filename = `Outputresults-${selectedActivityTitle}-${companyName}-${formatDate(new Date())}.${format}`;
    downloaderFor(format)(outputResultsArray, filename);
    // Clean up state immediately after triggering download
    resetOutputsState();
  };

  const activityHandler = (e) => {
    const id = e.target.value;
    const title = e.target.options[e.target.selectedIndex]?.dataset?.title || '';
    setSelectedActivity(id);
    setSelectedActivityTitle(title);
    // Clear any previously generated dataset when selection changes
    setOutputResultsArray([]);
    setDatasetCreated(false);
  };

  const activityMeta = async (id) => {
    let title = '';
    await db.collection('Activities')
      .where('ID', '==', id)
      .get()
      .then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          title = doc.data().Activity;
        });
      });
    return title;
  };

  // Fetch results once per output and group by date in memory.
  // Returns { dates: [uniqueDates asc], firstByDate: { date: firstResult } }
  const fetchResultsGrouped = async (outputID) => {
    const snap = await db.collection('Results')
      .where('CompagnyID', '==', client)
      .where('OutputID', '==', outputID)
      .orderBy('Timestamp', 'asc')
      .get();

    const firstByDate = {};
    const dates = [];
    snap.docs.filter(doc => notDeleted(doc.data())).forEach(doc => {
      const d = doc.data();
      const date = d.Timestamp.toDate().toLocaleDateString("nl-NL", options);
      if (!(date in firstByDate)) {
        firstByDate[date] = d.Result; // first (earliest) result for the date
        dates.push(date);
      }
    });
    return { dates, firstByDate };
  };

  const orderOutputResults = async () => {
    const array = [];
    await Promise.all(
      (outputs || []).filter((doc) => !doc.Deleted).map(async (doc) => {
        const object = {
          Activity: await activityMeta(doc.ActivityID),
          Output: doc.Title,
        };
        const { dates, firstByDate } = await fetchResultsGrouped(doc.ID);
        dates.forEach(date => {
          // Empty string distinguishes "no answer" from an actual 0
          object[date] = firstByDate[date] ?? '';
        });
        array.push(object);
      })
    );
    setOutputResultsArray(array);
  };

  const createDatasetOutputs = async () => {
    if (loading) return; // guard against double-click
    setLoading(true);
    await orderOutputResults();
    setLoading(false);
    setDatasetCreated(true);
  };

  const formatDate = (date) => {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // -------- MATERIALS: all household materials, joined + labelled --------

  // Direct reads (not hooks): scope by CompagnyID and filter Deleted by hand.
  // AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
  //
  // Named *Docs (snapshot -> array) to stay distinct from the imported, shared
  // notDeleted(doc) -> boolean above, which it delegates to. Do NOT rename this
  // back to `notDeleted`: as a same-scope const it shadows that import for the
  // whole component, and the callsites below pass a single doc, not a snapshot.
  // AI-CONTEXT: See docs/pitfalls.md#git-merge-parallel-fixes-collide
  const notDeletedDocs = (snap) => snap.docs
    .map((doc) => doc.data())
    .filter(notDeleted);

  const fetchTenantCollection = async (collection) => {
    const snap = await db.collection(collection).where('CompagnyID', '==', client).get();
    return notDeletedDocs(snap);
  };

  // Materials juist ÓÓK verwijderd: deze lijst is alleen de koppeling, en een
  // zacht verwijderd materiaal staat nog bij bewoners geregistreerd. Gefilterd
  // kwam zo'n regel in de csv als 'Onbekend materiaal' zonder prijs (inbox #284).
  // De registraties zelf blijven wél gefilterd.
  // AI-CONTEXT: See docs/pitfalls.md#database-hard-to-soft-delete-leaves-old-orphans (stap 4-5)
  const fetchAllMaterials = async () => {
    const snap = await db.collection('Materials').where('CompagnyID', '==', client).get();
    return snap.docs.map((doc) => doc.data());
  };

  // PersonaData is the biggest tenant collection (every data field of every
  // household); the export only needs the address, name and phone titles, so
  // narrow the query instead of reading everything (equality + `in` needs no
  // index).
  const fetchAdressFieldRows = async () => {
    const snap = await db.collection('PersonaData')
      .where('CompagnyID', '==', client)
      .where('Title', 'in', ADRESS_FIELD_TITLES)
      .get();
    return notDeletedDocs(snap);
  };

  const createDatasetMaterials = async () => {
    if (loadingMaterials) return; // guard against double-click
    setLoadingMaterials(true);
    try {
      const [entries, materialsList, adresses, personaData] = await Promise.all([
        fetchTenantCollection('AdressMaterials'),
        fetchAllMaterials(),
        fetchTenantCollection('Adresses'),
        fetchAdressFieldRows(),
      ]);
      const adressLabels = buildAdressLabelMap(personaData, adresses);
      const adressContacts = buildAdressContactMap(personaData, adresses);
      const rows = buildMaterialsExportRows({
        entries,
        materials: materialsList,
        adressLabels,
        adressContacts,
      });

      // Auditlog. Dit is een tenantbrede lees van Adresses + PersonaData en dus
      // letterlijk de 'massale export' waar de Security Policy een alert op
      // belooft. De Cloud Audit Logs zien hier alleen een query langskomen, geen
      // export -- daarom een eigen event met een telling.
      // Zie docs/features/clientdossier-auditlog.md
      callLogRecordOpen({
        client,
        recordType: 'household',
        event: 'clientRecordExported',
        rowCount: rows.length,
      }).catch((e) => console.warn('export niet gelogd', e));

      setMaterialsArray(rows);
      setMaterialsCreated(true);
    } finally {
      // No catch: a failed read should stay visible in the console, but the
      // spinner (doubling as the double-click guard) must never lock forever.
      setLoadingMaterials(false);
    }
  };

  const downloadMaterialsCSV = () => {
    const filename = `Materialen-${companyName}-${formatDate(new Date())}.csv`;
    downloadCSV(materialsArray, filename, MATERIALS_HEADER);
    // Clean up state immediately after triggering download
    setMaterialsArray([]);
    setMaterialsCreated(false);
  };

  // -------- RESEARCH: matrix-aware export (with Moment as 2nd column) --------

  // caches so we don't hammer Firestore
  const fieldMetaCacheRef = useRef(new Map()); // FieldID -> { title, position }
  const rowCacheRef = useRef(new Map());       // FieldID -> { byId: {rowId: {Title, Position}}, list: [...] }

  const resetResearchState = () => {
    setResearchResultsArray([]);
    setResearchHeaderOrder([]);
    setSelectedResearch('');
    setSelectedResearchTitle('');
    setLoadingResearch(false);
    setResearchDataCreated(false);
    // clear caches
    fieldMetaCacheRef.current.clear();
    rowCacheRef.current.clear();
  };

  const researchHandler = (e) => {
    const id = e.target.value;
    const title = e.target.options[e.target.selectedIndex]?.dataset?.title || '';
    setSelectedResearch(id);
    setSelectedResearchTitle(title);
    // Clear any previously generated dataset when selection changes
    setResearchResultsArray([]);
    setResearchHeaderOrder([]);
    setResearchDataCreated(false);
    // Caches are field-scoped so they're fine to keep
  };

  /**
   * Excel naast CSV: een CSV landt in een Nederlandse Excel in één kolom, en een
   * antwoord met een Enter erin valt daar over meerdere rijen uiteen.
   * @param {'xlsx' | 'csv'} format - Het bestandsformaat dat de gebruiker koos
   */
  const downloadResearchResults = (format) => {
    const filename = `Researchresults-${selectedResearchTitle}-${companyName}-${formatDate(new Date())}.${format}`;
    if (researchResultsArray.length > 0) {
      downloaderFor(format)(researchResultsArray, filename, researchHeaderOrder);
      // Clean up state immediately after triggering download
      resetResearchState();
    }
  };

  // Cache the in-flight Promise so concurrent callers share one Firestore read.
  // Without this, N responses for the same fieldId all fire the query before
  // any of them resolves and populates the cache.
  const getFieldMeta = (fieldId) => {
    if (fieldMetaCacheRef.current.has(fieldId)) return fieldMetaCacheRef.current.get(fieldId);
    const promise = (async () => {
      let title = '';
      let position = 0;
      const snap = await db.collection('QuestionnaireFields').where('ID', '==', fieldId).get();
      snap.forEach(d => {
        const dt = d.data();
        title = dt.Question;
        position = dt.Position || 0;
      });
      return { title, position };
    })();
    fieldMetaCacheRef.current.set(fieldId, promise);
    return promise;
  };

  const getMatrixRows = (fieldId) => {
    if (rowCacheRef.current.has(fieldId)) return rowCacheRef.current.get(fieldId);
    const promise = (async () => {
      const byId = {};
      const list = [];
      const snap = await db.collection('MatrixQuestionRows').where('Field', '==', fieldId).get();
      snap.docs.filter(d => notDeleted(d.data())).forEach(d => {
        const row = d.data(); // { ID, Field, Title, Position, ... }
        byId[row.ID] = { Title: row.Title, Position: row.Position || 0 };
        list.push({ id: row.ID, title: row.Title, position: row.Position || 0 });
      });
      return { byId, list };
    })();
    rowCacheRef.current.set(fieldId, promise);
    return promise;
  };

  // Helper to get Moment title from ID
  const getMomentTitle = (momentId) => {
    if (!momentId) return '';
    const m = (moments || []).find(mm => mm.ID === momentId);
    return m ? m.Title : '';
  };

  const getResearchResults = async () => {
    if (loadingResearch) return; // guard against double-click
    setLoadingResearch(true);

    const responses = questionnairesResponses || [];

    // Eerst de definities ophalen, dan pas samenstellen. De aggregatie zelf is
    // een pure functie, zodat ze te toetsen is zonder Firestore erbij.
    const fieldIds = [...new Set(responses.map(d => d.FieldID).filter(Boolean))];
    const matrixFieldIds = [...new Set(
      responses
        .filter(d => d.FieldType === 'matrix-one' || d.FieldType === 'matrix-multiple')
        .map(d => d.FieldID)
        .filter(Boolean)
    )];

    const [metaEntries, rowEntries] = await Promise.all([
      Promise.all(fieldIds.map(async id => [id, await getFieldMeta(id)])),
      Promise.all(matrixFieldIds.map(async id => [id, await getMatrixRows(id)])),
    ]);

    const { headerOrder, rows, skipped } = buildResearchResultRows({
      responses,
      fieldMeta: new Map(metaEntries),
      matrixRows: new Map(rowEntries),
      momentTitle: getMomentTitle,
    });

    setResearchHeaderOrder(headerOrder);
    setResearchResultsArray(rows);
    setLoadingResearch(false);
    setResearchDataCreated(true);

    // Alleen melden wat er écht uit is gevallen: een antwoord zonder rij-ID
    // maar mét rijtitel houdt zijn eigen kolom en hoort hier niet bij.
    if (skipped.length > 0) {
      alert(
        `Let op: ${skipped.length} matrix-antwoord(en) zijn overgeslagen ` +
        `omdat er geen rij-ID en geen rijtitel aanwezig was. De export is mogelijk onvolledig.`
      );
    }
  };

  return (
     <>
      {selectedOption === '3' &&
          <div className="settings-container">
                <div className="table-container dashboard-container">
                  <h2>Outputresultaten</h2>
                  <p>Download outputresultaten als Excel- of CSV-bestand</p>
                  <p><b>Selecteer een activiteit</b></p>
                  <select name="" id="" value={selectedActivity} onChange={activityHandler}>
                    <option value="">-- Selecteer activiteit --</option>
                    {(activities || []).map(item => (
                      <option key={item.ID} value={item.ID} data-title={item.Activity}>{item.Activity}</option>
                    ))}
                  </select>
                  {selectedActivity !== '' && !loading && !datasetCreated && (
                    <div className="create-dataset-container">
                      <button onClick={createDatasetOutputs}>Creëer dataset</button>
                    </div>
                  )}
                  {loading && (
                    <img className="loader-svg" src={loadingSpinner} alt="" />
                  )}
                  {datasetCreated && !loading && (
                    <div className='download-svg-container'>
                      <p>Download een bestand met alle outputresultaten</p>
                      <button
                        onClick={() => downloadOutputResults('xlsx')}
                        disabled={outputResultsArray.length === 0}
                      >Download als Excel</button>
                      <button
                        onClick={() => downloadOutputResults('csv')}
                        disabled={outputResultsArray.length === 0}
                      >Download als CSV</button>
                    </div>
                  )}
                </div>

                <div className="table-container dashboard-container">
                  <h2>Onderzoeksresultaten</h2>
                  <p>Download onderzoeksresultaten als Excel- of CSV-bestand</p>
                  <p><b>Selecteer een onderzoek</b></p>
                  <select name="" id="" value={selectedResearch} onChange={researchHandler}>
                    <option value="">-- Selecteer onderzoek --</option>
                    {(research || []).map(item => (
                      <option key={item.ID} value={item.ID} data-title={item.Title}>{item.Title}</option>
                    ))}
                  </select>
                  {selectedResearch !== '' && !loadingResearch && !researchDataCreated && (
                    <div className="create-dataset-container">
                      <button onClick={getResearchResults}>Creëer dataset</button>
                    </div>
                  )}
                  {loadingResearch && (
                    <img id='loading-icon' src={loadingSpinner} alt="" />
                  )}
                  {researchDataCreated && !loadingResearch && (
                    <div className='download-svg-container'>
                      <p>Download een bestand met alle onderzoeksresultaten</p>
                      <button
                        onClick={() => downloadResearchResults('xlsx')}
                        disabled={researchResultsArray.length === 0}
                      >Download als Excel</button>
                      <button
                        onClick={() => downloadResearchResults('csv')}
                        disabled={researchResultsArray.length === 0}
                      >Download als CSV</button>
                    </div>
                  )}
                </div>

                <div className="table-container dashboard-container">
                  <h2>Materialen</h2>
                  <p>Download alle materialen per huishouden als CSV-bestand, inclusief naam en telefoonnummer van de bewoner, productnummer, aantal, status en omschrijving (bijv. locatie in de woning)</p>
                  {!loadingMaterials && !materialsCreated && (
                    <div className="create-dataset-container">
                      <button onClick={createDatasetMaterials}>Creëer dataset</button>
                    </div>
                  )}
                  {loadingMaterials && (
                    <img className="loader-svg" src={loadingSpinner} alt="" />
                  )}
                  {materialsCreated && !loadingMaterials && (
                    <div className='download-svg-container'>
                      <p>Download een CSV bestand met alle materialen per huishouden</p>
                      <button
                        onClick={downloadMaterialsCSV}
                        disabled={materialsArray.length === 0}
                      >Download resultaten</button>
                    </div>
                  )}
                </div>
          </div>
        }
      </>
  );
};

export default Data;




